import React from "react";
import { IconButton, Box } from "@mui/material";
import { useIntl } from "react-intl";
import CachedIcon from "@mui/icons-material/Cached";
import { AnalyticsIcon } from "assets";

const styles = (topOffset: string = "36px") => {
  return {
    actionContainer: {
      position: "absolute",
      right: "7px",
      top: topOffset,
    },
    iconButton: {
      display: "grid",
      cursor: "pointer",
      marginBottom: "5px",
      borderRadius: "2px",
      padding: "1px",
      "& svg": {
        width: "24.875px",
        height: "25.653px",
      },
      "& i": {
        width: "24.875px",
        height: "25.653px",
        display: "inline-block",
        padding: "3px",
        paddingLeft: "2px",
      },
    },
  };
};

interface ProductWithCachedAt {
  cachedAt?: Date | string;
}

interface ProductActionIconsProps {
  product?: ProductWithCachedAt;
  isAnalyticsEnabled: boolean;
  onRefresh: (event: React.MouseEvent) => void;
  onAnalytics: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  onCategories: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  topOffset?: string;
}

export default function ProductActionIcons({
  product,
  isAnalyticsEnabled,
  onRefresh,
  onAnalytics,
  onCategories,
  topOffset
}: ProductActionIconsProps) {
  const intl = useIntl();
  const classes = styles(topOffset);

  return (
    <Box sx={{ ...classes.actionContainer }}>
      {product && product.cachedAt && (
        <IconButton
          size="small"
          color="menu"
          title={intl.formatMessage({
            id: "productActions.refreshIcon",
            defaultMessage: "Refresh Product",
          })}
          aria-label="RefreshProduct"
          component="div"
          sx={{
            ...classes.iconButton,
          }}
        >
          <CachedIcon
            titleAccess={
              product?.cachedAt
                ? `Last cached at ${new Date(product?.cachedAt as Date)}`
                : "Product unavailable"
            }
            onClick={onRefresh}
            className="refreshIcon"
          />
        </IconButton>
      )}
      {isAnalyticsEnabled && (
        <IconButton
          size="small"
          color="menu"
          title={intl.formatMessage({
            id: "productActions.analytics",
            defaultMessage: "View Analytics",
          })}
          aria-label="ProductAnalytics"
          component="div"
          onClick={(event) => {
            event.stopPropagation();
            onAnalytics(event);
          }}
          sx={{
            ...classes.iconButton,
          }}
        >
          <AnalyticsIcon />
        </IconButton>
      )}
      {product && product.cachedAt && (
        <IconButton
          size="small"
          color="menu"
          title={intl.formatMessage({
            id: "productActions.categories",
            defaultMessage: "View Categories",
          })}
          aria-label="ProductCategories"
          component="div"
          onClick={(event) => {
            event.stopPropagation();
            onCategories(event);
          }}
          sx={{
            ...classes.iconButton,
          }}
        >
          <i className="fas fa-sitemap" />
        </IconButton>
      )}
    </Box>
  );
}
